import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { supabase } from '../utils/supabaseClient';
import { generateCSVExport, deleteProjection } from '../utils/projectionUtils';
import { generateProjectionPDF, downloadPDF } from '../utils/pdfUtils';

function ProjectionDetails({ projectionId }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [projection, setProjection] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [locationSummary, setLocationSummary] = useState(null);
  const [error, setError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  useEffect(() => {
    if (projectionId) {
      fetchProjectionData();
    }
  }, [projectionId]);
  
  const fetchProjectionData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch the projection details
      const { data: projectionData, error: projectionError } = await supabase
        .from('projections')
        .select('*')
        .eq('id', projectionId)
        .single();
      
      if (projectionError) throw projectionError;
      
      setProjection(projectionData);
      
      // Fetch all scenarios for this projection
      const { data: scenariosData, error: scenariosError } = await supabase
        .from('projection_scenarios')
        .select('*')
        .eq('projection_id', projectionId)
        .order('created_at', { ascending: false });
      
      if (scenariosError) throw scenariosError;
      
      setScenarios(scenariosData || []);
      
      // Fetch location data for summary calculation
      const { data: locationsData, error: locationsError } = await supabase
        .from('projection_locations')
        .select('id, is_future, logistixai_order_number, customer_address')
        .eq('projection_id', projectionId);
      
      if (locationsError) throw locationsError;
      
      // Process the location data with unique locations and units
      const uniqueLocations = new Set();
      const uniqueUnits = new Set();
      const futureLocations = new Set();
      
      locationsData.forEach(location => {
        if (location.customer_address) {
          uniqueLocations.add(location.customer_address.trim());
          
          if (location.is_future) {
            futureLocations.add(location.customer_address.trim());
          }
        }
        
        if (location.logistixai_order_number) {
          uniqueUnits.add(location.logistixai_order_number);
        }
      });
      
      const locationSummaryData = {
        totalLocations: uniqueLocations.size,
        futureLocations: futureLocations.size,
        totalUnits: uniqueUnits.size
      };
      
      setLocationSummary(locationSummaryData);
      
    } catch (error) {
      console.error('Error fetching projection data:', error);
      setError('Failed to load projection details: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleExportCsv = async (scenarioId) => {
    if (!projectionId || !scenarioId) return;
    
    try {
      setIsExporting(true);
      setError(null);
      
      // Generate CSV content
      const csvContent = await generateCSVExport(projectionId, scenarioId);
      
      // Trigger download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `projection_${projectionId}_scenario_${scenarioId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setError('Failed to export CSV: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };
  
  const handleExportPdf = async (scenarioId = null) => {
    try {
      setIsPdfExporting(true);
      setError(null);
      
      // Generate PDF
      const pdfBlob = await generateProjectionPDF(projectionId, scenarioId);
      
      // Download the PDF
      const fileName = scenarioId 
        ? `projection_${projectionId}_scenario_${scenarioId}.pdf` 
        : `projection_${projectionId}_summary.pdf`;
      
      downloadPDF(pdfBlob, fileName);
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setError('Failed to export PDF: ' + error.message);
    } finally {
      setIsPdfExporting(false);
    }
  };
  
  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!projectionId || !projection) return;
    
    try {
      setIsDeleting(true);
      setError(null);
      
      const result = await deleteProjection(projectionId);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete projection');
      }
      
      // Navigate back to projections list
      navigate('/projections');
      
    } catch (error) {
      console.error('Error deleting projection:', error);
      setError(`Failed to delete projection: ${error.message}`);
      setDeleteConfirmOpen(false);
      setIsDeleting(false);
    }
  };
  
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat('en-US').format(num);
  };
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }
  
  if (!projection) {
    return (
      <Alert severity="warning">
        Projection not found.
      </Alert>
    );
  }
  
  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Projection Details
            </Typography>
            
            <Typography variant="subtitle1" gutterBottom>
              File: {projection.file_name.split('_').slice(1).join('_')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Uploaded: {formatDate(projection.uploaded_at)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Status: {projection.status}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => handleExportPdf()}
              disabled={isPdfExporting}
            >
              {isPdfExporting ? 'Exporting...' : 'Export Summary PDF'}
            </Button>
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
            >
              Delete Projection
            </Button>
          </Box>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Base Projection
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1">Total Locations:</Typography>
                    <Typography variant="body1">{formatNumber(projection.total_locations)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1">Total Revenue:</Typography>
                    <Typography variant="body1">{formatCurrency(projection.total_projected_revenue)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1">Total Expense:</Typography>
                    <Typography variant="body1">{formatCurrency(projection.total_projected_expense)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Gross Profit:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(projection.total_projected_margin)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Margin:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {projection.total_projected_revenue > 0 
                        ? `${((projection.total_projected_margin / projection.total_projected_revenue) * 100).toFixed(2)}%` 
                        : '0.00%'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Location Summary
                </Typography>
                {locationSummary ? (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body1">Total Locations in File:</Typography>
                      <Typography variant="body1">{formatNumber(locationSummary.totalLocations)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body1">Future Locations:</Typography>
                      <Typography variant="body1">{formatNumber(locationSummary.futureLocations)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body1">Total Units:</Typography>
                      <Typography variant="body1">{formatNumber(locationSummary.totalUnits || 'N/A')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body1">Revenue per Location:</Typography>
                      <Typography variant="body1">$300.00</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">Revenue per Refusal Unit:</Typography>
                      <Typography variant="body1">$50.00</Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body1">No location data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Saved Scenarios
        </Typography>
        
        {scenarios.length === 0 ? (
          <Typography variant="body1">
            No scenarios have been saved yet. Use the Estimator tool to create and save scenarios.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Completed %</TableCell>
                  <TableCell align="right">Refused %</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Gross Profit</TableCell>
                  <TableCell align="right">Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scenarios.map((scenario) => (
                  <TableRow key={scenario.id}>
                    <TableCell>{scenario.name}</TableCell>
                    <TableCell align="right">{scenario.percent_completed}%</TableCell>
                    <TableCell align="right">{scenario.percent_refused}%</TableCell>
                    <TableCell align="right">{formatCurrency(scenario.total_revenue)}</TableCell>
                    <TableCell align="right">{formatCurrency(scenario.total_margin)}</TableCell>
                    <TableCell align="right">{formatDate(scenario.created_at)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleExportCsv(scenario.id)}
                          disabled={isExporting}
                        >
                          CSV
                        </Button>
                        <Button
                          size="small"
                          startIcon={<PictureAsPdfIcon />}
                          onClick={() => handleExportPdf(scenario.id)}
                          disabled={isPdfExporting}
                        >
                          PDF
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-projection-dialog-title"
        aria-describedby="delete-projection-dialog-description"
      >
        <DialogTitle id="delete-projection-dialog-title">
          Delete Projection
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-projection-dialog-description">
            Are you sure you want to delete this projection
            {projection ? ` "${projection.file_name.split('_').slice(1).join('_')}"` : ''}?
            This action cannot be undone and will permanently delete all associated data.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProjectionDetails; 